import { useState } from 'react';
import { Check, RefreshCw, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Modal, Media } from '../components/common';
import { useLab } from '../lib/service';
import {} from '../shared';
import type { ShotView } from '../../../../packages/domain/src/view';
import { episodeAllowance } from '../../../../packages/domain/src/production-plan';
import { MODELS, money } from '@drama/domain';
import { useSeries } from './context';

/** Full-screen review of a single shot and its versions. */
export function ShotModal({
  shot,
  onClose,
  onError,
  onRegenerate,
  disabled,
}: {
  shot: ShotView;
  onClose: () => void;
  onError: (e: unknown) => void;
  onRegenerate: (id: string, instruction: string) => Promise<boolean>;
  disabled: boolean;
}) {
  const lab = useLab();
  const { project, available, effectiveModel, providerProfile } = useSeries();
  const [instruction, setInstruction] = useState(''),
    [version, setVersion] = useState(shot.currentVersion);
  const versions = lab.detail!.versions.filter((v) => v.shotId === shot._id);
  const v = versions.find((v) => v.version === version);
  const [busy, setBusy] = useState(false);
  // Force this shot to be new in the existing conservative quote. Finishing is included
  // because the changed shot will need a rebuilt cut; all other shots remain reusable.
  const allowance = episodeAllowance(
    [shot],
    [],
    lab.detail!.assets,
    project.seasonVersion,
    effectiveModel,
    lab.detail!.plates,
    providerProfile,
  ).allowance;
  const overBudget = !lab.isDemo && (allowance > available || !!lab.home?.budget.suspended);
  const unsupportedModel = effectiveModel === MODELS.controlledVideo && shot.spec.duration > 8;
  return (
    <Modal
      wide
      title={`Shot ${String(shot.spec.order + 1).padStart(2, '0')} · ${shot.spec.framing}`}
      subtitle={`${shot.spec.duration} seconds · ${shot.spec.location}`}
      onClose={onClose}
    >
      <div className="shot-inspector">
        <div>
          {v?.videoAssetId ? (
            <Media assetId={v.videoAssetId} video />
          ) : (
            <Media assetId={v?.referenceAssetId} />
          )}
          <label className="field-label" htmlFor="shot-version">
            Version history
          </label>
          <select
            id="shot-version"
            value={version}
            onChange={(e) => setVersion(Number(e.target.value))}
          >
            {versions.length ? (
              versions.map((v) => (
                <option key={v._id} value={v.version}>
                  Version {v.version}
                  {v.humanAccepted ? ' · accepted' : ''}
                </option>
              ))
            ) : (
              <option value={0}>Not yet generated</option>
            )}
          </select>
        </div>
        <div>
          <span className="eyebrow">THE PERFORMANCE</span>
          <p>{v?.spec.action ?? shot.spec.action}</p>
          <dl className="shot-specs">
            <dt>Emotion</dt>
            <dd>{shot.spec.emotion}</dd>
            <dt>Wardrobe</dt>
            <dd>{shot.spec.wardrobe}</dd>
            <dt>Physical continuity</dt>
            <dd>{shot.spec.physicalState}</dd>
            <dt>Camera</dt>
            <dd>{shot.spec.movement}</dd>
          </dl>
          <div className="qa-card">
            <ShieldCheck size={19} />
            <div>
              <strong>Continuity & quality</strong>
              <p>
                {v?.qaVideo?.summary ?? v?.qaReference?.summary ?? 'Checks run after generation.'}
              </p>
              {v?.qaVideo?.issues.map((i, n) => (
                <small key={n}>
                  {i.severity} · {i.description}
                </small>
              ))}
            </div>
          </div>
          <label className="field-label" htmlFor="shot-instruction">
            What should change in this shot?
          </label>
          <textarea
            id="shot-instruction"
            placeholder="Make her hesitation last a little longer…"
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            rows={3}
            maxLength={4000}
            disabled={disabled || busy}
          />
          <div className="shot-change-allowance" aria-label="Shot change allowance">
            <strong>
              {lab.isDemo
                ? 'Simulation · no AI charges'
                : `${money(allowance)} estimated allowance`}
            </strong>
            <p>
              A new take of shot {String(shot.spec.order + 1).padStart(2, '0')}, recovery headroom,
              and finishing the updated cut. Other shots are kept.
            </p>
            <p>
              {money(available)} available within your current limits. The cut must be rebuilt
              before episode approval.
            </p>
            {overBudget && (
              <p role="status">
                This estimate exceeds the available allowance or production is suspended. Review
                your budget before requesting a change.
              </p>
            )}
            {unsupportedModel && (
              <p role="status">Choose Gemini Omni for this shot’s longer take.</p>
            )}
          </div>
          <div className="inline-actions">
            <Button
              disabled={disabled || busy || !instruction.trim() || overBudget || unsupportedModel}
              onClick={() => {
                setBusy(true);
                void onRegenerate(shot._id, instruction.trim())
                  .then((success) => {
                    if (success) onClose();
                  })
                  .catch(onError)
                  .finally(() => setBusy(false));
              }}
            >
              <RefreshCw size={14} />
              Generate new take
            </Button>
            <Button
              variant="outline"
              disabled={
                disabled ||
                busy ||
                !v?.videoAssetId ||
                version !== shot.currentVersion ||
                shot.status === 'ACCEPTED'
              }
              onClick={() => {
                setBusy(true);
                void lab
                  .acceptShot(shot._id)
                  .then(onClose)
                  .catch(onError)
                  .finally(() => setBusy(false));
              }}
            >
              <Check size={15} />
              Accept shot
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
